import { FocusZone as FluentFocusZone, FocusZoneDirection as FluentFocusZoneDir, FocusZoneTabbableElements as FluentFocusZoneTabbableEl, IFocusZone } from "@fluentui/react";
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { useComponentLogger } from "../../shared/logging";
import { BaseProps, validateProps } from "../../shared/props";

export interface FocusZoneProps extends BaseProps {
    /**
     * The component that should be used as the root element of the FocusZone; default is div
     */
    readonly as?: React.ElementType;
    /**
     * Sets the provided selector as the initial active element
     */
    readonly defaultActiveElement?: string;
    /**
     * Sets which arrows to react to; options are bidirectional (all), domOrder (all via DOM-order:
     * L/R are swapped for RTL), horizontal (left/right), vertical (up/down); default is bidirectional
     */
    readonly direction?: FocusZoneDirection;
    /**
     * Sets the FocusZone to be un-tabbable; keyboard nav will be disabled
     */
    readonly disabled?: boolean;
    /**
     * Cycles to the beginning of the targets from the end and vice versa; default set to false
     */
    readonly isCircularNavigation?: boolean;
    /**
     * Callback to be invoked when the immediate child element has focus or
     * one of its respective children elements has focus
     */
    readonly onActiveElementChanged?: (element?: HTMLElement, ev?: React.FocusEvent<HTMLElement>) => void;
    /**
     * Callback to be invoked on keypresses to determine if user intends to navigate into the inner zone
     * Returning true will ask the first inner zone to set focus
     */
    readonly shouldEnterInnerZone?: (ev: React.KeyboardEvent<HTMLElement>) => boolean;
    /**
     * Determines if a default tabbable element should be force focused on FocusZone mount.
     * Default is false.
     */
    readonly shouldFocusOnMount?: boolean;
    /**
     * Allows tab key to be handled to tab through a list of items in the focus zone, an
     * unfortunate side effect is that users will not be able to tab out of the focus zone
     * and have to hit escape or some other key.
     */
    readonly handleTabKey?: FocusZoneTabbableElements;
}

export interface FocusZoneRef {
    readonly focus: () => void;
}

export type FocusZoneDirection = keyof typeof FluentFocusZoneDir;
export type FocusZoneTabbableElements = keyof typeof FluentFocusZoneTabbableEl;

export const FocusZone: React.FC<FocusZoneProps & React.RefAttributes<FocusZoneRef>> = forwardRef<FocusZoneRef, FocusZoneProps>(({
    as = "div",
    direction = "bidirectional",
    handleTabKey,
    shouldEnterInnerZone,
    children,
    ...otherProps
}, ref) => {
    useComponentLogger("FocusZone", otherProps.id);

    const focusZoneRef = useRef<IFocusZone>(null);
    useImperativeHandle(ref, () => ({
        focus: () => {
            focusZoneRef.current?.focus();
        }
    }));

    validateProps("FocusZone", otherProps);
    return (
        <FluentFocusZone
            as={as}
            direction={FluentFocusZoneDir[direction]}
            handleTabKey={handleTabKey ? FluentFocusZoneTabbableEl[handleTabKey] : undefined}
            isInnerZoneKeystroke={shouldEnterInnerZone}
            {...otherProps}
            componentRef={focusZoneRef}
        >
            {children}
        </FluentFocusZone>

    );
});
